// Attaches the value the user pointed at ("Explain this value") to the view at
// send time, after mergeValuesIntoView has run.
//
// Three client-only keys are dropped here so they never reach the wire: entityId
// (the guard that a focus captured on one dashboard is not sent with another's
// view), cardId (the join key into the values registry) and term (the clicked
// term as the chart reported it). The on-screen figure for the focused member
// is looked up in that registry by the clicked term first — the digest labels
// rows by the raw term / response date — then the raw value and the display
// label, so the agent is handed the exact number the user is asking about
// rather than being left to re-derive it.

import {
  InsightsGadgetValues,
  InsightsView,
  InsightsViewFocus,
  InsightsViewValues,
} from 'contextProviders/InsightsContentProvider/viewTypes';
import isSameRowLabel from 'hooks/useRegisterInsightsGadgetValues/isSameRowLabel';

// Every label the focused row might be filed under in the digest. Used both to
// pin the row through sampling (getViewValues) and to read it back here.
export const focusRowLabels = (focus: InsightsViewFocus): string[] => {
  const labels: string[] = [];
  if (focus.term !== undefined) {
    labels.push(focus.term);
  }
  if (focus.value) {
    labels.push(String(focus.value.rawValue), focus.value.label);
  }
  if (focus.dateBucket) {
    labels.push(focus.dateBucket.startDate);
    if (focus.dateBucket.label) {
      labels.push(focus.dateBucket.label);
    }
  }
  return [...new Set(labels.filter((label) => label !== ''))];
};

const hasFigures = (
  values: InsightsGadgetValues,
): values is InsightsViewValues => !!values && !('isLoading' in values);

const readOnScreenValues = ({
  focus,
  values,
}: {
  focus: InsightsViewFocus;
  values: InsightsGadgetValues;
}): { metric: string; value: number }[] | undefined => {
  if (!hasFigures(values)) {
    return undefined;
  }
  // A single-figure gadget (smart card): the total IS the value.
  if (values.total && !focus.value && !focus.dateBucket) {
    return [values.total];
  }
  const rows = values.rows ?? [];
  const labels = focusRowLabels(focus);
  // The same matcher pinned the row through sampling (pinRows), so a row the
  // gadget has on screen is always found here.
  for (const label of labels) {
    const match = rows.find((row) =>
      isSameRowLabel({ rowLabel: row.label, label }),
    );
    if (match) {
      return match.values;
    }
  }
  if (values.total) {
    return [values.total];
  }
  return undefined;
};

const mergeFocusIntoView = ({
  view,
  focus,
  entityId,
  valuesByCardId,
}: {
  view: InsightsView;
  focus: InsightsViewFocus | null | undefined;
  // The entity whose view is being sent.
  entityId: string;
  // The raw registry read (pre-merge), still keyed by card.
  valuesByCardId: { [cardId: string]: InsightsGadgetValues };
}): InsightsView => {
  if (!focus) {
    return view;
  }
  // A focus captured on another entity must never ride along with this view.
  if (focus.entityId !== undefined && focus.entityId !== entityId) {
    return view;
  }

  const { entityId: _entityId, cardId, term: _term, ...wire } = focus;
  const onScreenValues =
    wire.onScreenValues ??
    (cardId
      ? readOnScreenValues({ focus, values: valuesByCardId[cardId] })
      : undefined);

  return {
    ...view,
    focus: {
      ...wire,
      ...(onScreenValues && onScreenValues.length > 0
        ? { onScreenValues }
        : {}),
    },
  };
};

export default mergeFocusIntoView;
